// src/services/auth-service.ts
// Auth con verificación continua y timers robustos

import Api, { setTokenProvider, setUnauthorizedHandler, apiUrl } from "../utils/fetch";
import Swal from "sweetalert2";

// ===================== Tipos =====================
export interface LoginCredentials { cod_farmacia: string; password: string }
export interface UsuarioData {
  cod_farmacia: string; nombre: string; mail: string;
  dto_cliente: string | number; grupo?: string; usuario_nombre?: string;
  rol?: string;
  menu: Array<{ nombre: string; url: string | null; orden: number; permisos: any[]; opciones?: any[] }>;
}
type LoginResponse = { success: boolean; token?: string; user?: UsuarioData; message?: string };

type SessionPayload = {
  expires_in?: number;
  expires_at?: string;
  expires_at_timestamp?: number;
};

// ===================== Storage keys =====================
const SS_AUTH = "admin_authenticated";
const K_TOKEN = "auth_token";
const K_EXP_TIMESTAMP = "auth_expiry_timestamp";
const K_USER = "user";
const K_ROLE = "role";
const K_UTYPE = "user_type";

// ===================== Helpers =====================
const now = () => Date.now();
const nowInSeconds = () => Math.floor(Date.now() / 1000);
const set = (k: string, v: string) => { try { localStorage.setItem(k, v); } catch { } };
const rm = (k: string) => { try { localStorage.removeItem(k); } catch { } };
const get = (k: string) => { try { return localStorage.getItem(k); } catch { return null } };

export const getToken = () => get(K_TOKEN);

const getExpiryTimestamp = (): number => {
  const stored = get(K_EXP_TIMESTAMP);
  return stored ? Number(stored) : 0;
};

export const getCurrentUser = (): UsuarioData | null => {
  try { const s = get(K_USER); return s ? JSON.parse(s) as UsuarioData : null; } catch { return null; }
};

export const isAuthenticated = (): boolean => {
  const ok = sessionStorage.getItem(SS_AUTH) === "true";
  const expiryTs = getExpiryTimestamp();

  if (expiryTs > 0 && nowInSeconds() >= expiryTs) {
    console.warn("[auth] ⏰ Sesión expirada según timestamp");
    return false;
  }

  return !!ok;
};

export const getTokenTimeRemaining = (): number => {
  const expiryTs = getExpiryTimestamp();
  if (expiryTs === 0) return 0;

  const remainingSeconds = expiryTs - nowInSeconds();
  return Math.max(0, remainingSeconds * 1000);
};

setTokenProvider(() => getToken());

// ✅ Configurar handler para respuestas 401 (sesión expirada en servidor)
setUnauthorizedHandler(() => {
  console.error("[auth] 🚨 Respuesta 401 - Token inválido o expirado en servidor");
  // Solo ejecutar si no estamos ya en proceso de logout
  if (!logoutInProgress) {
    logout(true, "Su sesión ha expirado").catch(console.error);
  }
});

// ===================== Config =====================
const WARN_BEFORE_MS = Math.max(0, Number((import.meta as any)?.env?.VITE_SESSION_WARN_MS ?? 60_000));
const USE_CHECK_MONITOR = false; // Hard disabled by user request
const CHECK_INTERVAL_MS = Number((import.meta as any)?.env?.VITE_CHECK_SESSION_INTERVAL_MS ?? 15000);

// Verificación continua cada segundo para asegurar el logout
const EXPIRY_CHECK_INTERVAL = 1000;

// ===================== Variables de control =====================
let checkSessionInterval: NodeJS.Timeout | null = null;
let expirationTimer: NodeJS.Timeout | null = null;
let warningTimer: NodeJS.Timeout | null = null;
let expiryCheckInterval: NodeJS.Timeout | null = null;
let logoutInProgress = false;
let alertShown = false;
let countdownShown = false;
let lastServerCheckTime = 0;
let consecutiveErrors = 0;
let isAutoLogout = false; // ✅ Flag para distinguir logout automático vs manual
let suppressLogoutAlert = false; // ✅ Flag para suprimir alertas de logout manual

// ===================== Helpers exportados =====================
export function setSuppressLogoutAlert(value: boolean) {
  suppressLogoutAlert = value;
}


// ===================== UI: Alertas =====================
async function mostrarAlertaSesionExpirada(motivo = "Su sesión ha expirado") {
  if (alertShown || suppressLogoutAlert) return;
  alertShown = true;
  await Swal.fire({
    icon: "warning",
    title: "Sesión expirada",
    text: `${motivo}. Por favor, vuelva a iniciar sesión.`,
    confirmButtonText: "Entendido",
    allowOutsideClick: false,
    allowEscapeKey: false,
    confirmButtonColor: "#3085d6"
  });
  alertShown = false;
}

function mostrarCuentaRegresivaExpiracion(msInicial: number) {
  if (WARN_BEFORE_MS <= 0 || countdownShown || msInicial <= 0) return;
  countdownShown = true;

  Swal.fire({
    icon: "info",
    title: "Tu sesión está por expirar",
    html: `Se cerrará en <b id="exp_secs"></b> segundos.`,
    timer: msInicial,
    timerProgressBar: true,
    allowOutsideClick: false,
    allowEscapeKey: false,
    showConfirmButton: false,
    didOpen: () => {
      const el = Swal.getHtmlContainer()?.querySelector("#exp_secs") as HTMLElement | null;
      const upd = () => {
        if (el) el.textContent = String(Math.ceil(getTokenTimeRemaining() / 1000));
      };
      upd();
      const i = setInterval(upd, 500);
      (Swal as any).__expInterval = i;
    },
    willClose: () => {
      countdownShown = false;
      try { clearInterval((Swal as any).__expInterval); } catch { }
    }
  });
}

// ===================== Verificación continua de expiración =====================
function startExpiryCheckInterval() {
  if (expiryCheckInterval !== null) {
    clearInterval(expiryCheckInterval);
  }

  expiryCheckInterval = setInterval(() => {
    if (logoutInProgress) return;

    const expiryTs = getExpiryTimestamp();
    if (expiryTs === 0) {
      console.warn("[auth] ⚠️ Intervalo activo pero no hay timestamp en localStorage");
      return;
    }

    const currentTs = nowInSeconds();
    const remaining = expiryTs - currentTs;

    // Si ya expiró, ejecutar logout inmediato
    if (remaining <= 0) {
      console.error("[auth] ⏰ ¡EXPIRACIÓN DETECTADA! Ejecutando logout inmediato");
      stopExpiryCheckInterval();
      if (!logoutInProgress) {
        logout(true, "Su sesión ha expirado").catch(console.error);
      }
      return;
    }

    // Mostrar advertencia si corresponde
    if (WARN_BEFORE_MS > 0 && remaining * 1000 <= WARN_BEFORE_MS && !countdownShown) {

      mostrarCuentaRegresivaExpiracion(remaining * 1000);
    }
  }, EXPIRY_CHECK_INTERVAL);
}

function stopExpiryCheckInterval() {
  if (expiryCheckInterval !== null) {
    clearInterval(expiryCheckInterval);
    expiryCheckInterval = null;
  }
}

// ===================== Sistema de timers =====================
export function scheduleTokenExpiration() {
  // Limpiar timers previos
  if (expirationTimer !== null) {
    clearTimeout(expirationTimer);
    expirationTimer = null;
  }
  if (warningTimer !== null) {
    clearTimeout(warningTimer);
    warningTimer = null;
  }

  const expiryTs = getExpiryTimestamp();
  if (expiryTs === 0) {
    console.warn("[auth] ⚠️ No hay timestamp de expiración");
    return;
  }

  const timeUntilExpiry = getTokenTimeRemaining();

  if (timeUntilExpiry <= 0) {
    console.error("[auth] ⏰ Token ya expirado - ejecutando logout inmediato");
    logout(true, "Su sesión ha expirado").catch(console.error);
    return;
  }

  // Iniciar verificación continua
  startExpiryCheckInterval();

  // Programar advertencia
  if (WARN_BEFORE_MS > 0 && timeUntilExpiry > WARN_BEFORE_MS) {
    const timeUntilWarning = timeUntilExpiry - WARN_BEFORE_MS;
    warningTimer = setTimeout(() => {
      mostrarCuentaRegresivaExpiracion(WARN_BEFORE_MS);
    }, timeUntilWarning);
  } else if (WARN_BEFORE_MS > 0 && timeUntilExpiry <= WARN_BEFORE_MS && !countdownShown) {
    mostrarCuentaRegresivaExpiracion(timeUntilExpiry);
  }

  // Programar logout exacto (como backup del check continuo)
  expirationTimer = setTimeout(() => {
    console.error("[auth] ⏰ Timer de expiración disparado - ejecutando logout");
    if (!logoutInProgress) {
      logout(true, "Su sesión ha expirado").catch(console.error);
    }
  }, timeUntilExpiry);
}

// ===================== Configurar expiración desde backend =====================
function setExpiryFromSession(session?: SessionPayload | null) {

  if (!session) {
    console.warn("[auth] ⚠️ No se recibió info de sesión del backend");
    return;
  }

  // PRIORIDAD 1: expires_at_timestamp
  if (Number.isFinite(session.expires_at_timestamp) && session.expires_at_timestamp! > 0) {
    const expiryTs = Number(session.expires_at_timestamp);
    set(K_EXP_TIMESTAMP, String(expiryTs));

    scheduleTokenExpiration();
    return;
  }

  console.error("[auth] ❌ expires_at_timestamp NO es válido - probando con expires_at...");

  // PRIORIDAD 2: expires_at
  if (session.expires_at) {
    const d = Date.parse(session.expires_at);
    if (Number.isFinite(d)) {
      const expiryTs = Math.floor(d / 1000);
      set(K_EXP_TIMESTAMP, String(expiryTs));
      scheduleTokenExpiration();
      return;
    }
  }

  // PRIORIDAD 3: expires_in
  if (Number.isFinite(session.expires_in) && session.expires_in! > 0) {
    const expiryTs = nowInSeconds() + Number(session.expires_in);
    set(K_EXP_TIMESTAMP, String(expiryTs));
    scheduleTokenExpiration();
    return;
  }

  console.error("[auth] ❌ No se pudo determinar expiración del backend");
}

export function maybeSyncExpiryFromResponse(meta?: { expires_at_ts?: number; expires_in?: number; expires_at?: string }) {
  if (!meta) return;
  setExpiryFromSession({
    expires_at_timestamp: meta.expires_at_ts,
    expires_in: meta.expires_in,
    expires_at: meta.expires_at
  });
}

// ===================== Monitor de pings =====================
function verificarCheckSessionSincrono() {
  // Disabled
}

export function startCheckSessionMonitor() {
  return;
}

export function stopCheckSessionMonitor() {
  if (checkSessionInterval !== null) {
    clearInterval(checkSessionInterval);
    checkSessionInterval = null;
  }
}

// ===================== Verificación usada por router =====================
export async function verificarRuta(): Promise<boolean> {
  return isAuthenticated();
}
export const checkSession = verificarRuta;


// ===================== Login / Logout =====================
export async function login(credentials: LoginCredentials): Promise<LoginResponse> {
  resetAuthSystem();

  const res = (await Api.post("login", credentials)) as any;
  if (!res?.message || !res?.cliente) {
    return { success: false, message: res?.message || "Credenciales inválidas" };
  }

  const token = res.token as string | undefined;
  if (token) set(K_TOKEN, token);

  set(K_USER, JSON.stringify(res.cliente));

  const rawRole = String(res?.cliente?.rol ?? res?.rol ?? "").toLowerCase();
  set(K_ROLE, rawRole);

  // user_type viene directo del backend ("admin" | "cliente"), o se deduce del rol como fallback
  const backendUserType = String(res?.user_type ?? res?.cliente?.user_type ?? "").toLowerCase();
  const userType: string =
    backendUserType === "admin" ? "admin"
      : backendUserType === "plex" ? "plex"
        : backendUserType === "cliente" ? (rawRole === "cliente_user" ? "limited" : "full")
          : rawRole === "admin" || rawRole === "administrador" ? "admin"
            : rawRole === "plex" || rawRole === "usuario_plex" ? "plex"
              : rawRole === "cliente_admin" ? "full"
                : rawRole === "cliente_user" ? "limited"
                  : "";
  if (userType) set(K_UTYPE, userType);

  setExpiryFromSession(res.session as SessionPayload);

  sessionStorage.setItem(SS_AUTH, "true");

  startCheckSessionMonitor();

  window.dispatchEvent(new CustomEvent("auth:session-ready", { detail: { ts: now(), reason: "login" } }));

  return { success: true, token, user: res.cliente };
}

export async function logout(showMsg = true, reason?: string) {
  if (logoutInProgress) return;
  logoutInProgress = true;

  // Detener TODOS los timers e intervalos
  stopCheckSessionMonitor();
  stopExpiryCheckInterval();

  if (expirationTimer !== null) {
    clearTimeout(expirationTimer);
    expirationTimer = null;
  }
  if (warningTimer !== null) {
    clearTimeout(warningTimer);
    warningTimer = null;
  }

  // Limpiar storage local
  [K_TOKEN, K_EXP_TIMESTAMP, K_USER, K_ROLE, K_UTYPE].forEach(rm);
  sessionStorage.removeItem(SS_AUTH);

  // Limpiar flags de popup vistos
  try {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('popup_closed_')) {
        localStorage.removeItem(key);
      }
    });
  } catch (e) {
    console.warn("No se pudieron leer keys de localStorage:", e);
  }

  // Resetear tema a claro al cerrar sesión
  try {
    localStorage.removeItem("client-theme");
    localStorage.removeItem("admin-dark-theme");
    localStorage.removeItem("pedidos_detalle_locales");
    localStorage.removeItem("combo_detalles_cache_v1");
    localStorage.removeItem("cached_cronogramas_online");
    document.documentElement.setAttribute("data-theme", "light");
    document.documentElement.setAttribute("theme", "light");
    document.body.setAttribute("data-theme", "light");
    document.body.setAttribute("theme", "light");
  } catch { }

  try {
    window.dispatchEvent(new CustomEvent("auth:session-cleared", {
      detail: { ts: now(), reason: reason || "logout" }
    }));
  } catch (e) {
    console.warn("[auth] Error en listener de auth:session-cleared", e);
  }

  // Solo mostrar alerta si se solicita Y no está suprimida
  if (showMsg && reason && !suppressLogoutAlert) {
    try {
      await mostrarAlertaSesionExpirada(reason);
    } catch (e) {
      console.warn("[auth] Error mostrando alerta", e);
    }
  }

  logoutInProgress = false;

  // Llamar al backend para invalidar la cookie (keepalive: sobrevive a la navegación)
  try {
    const logoutUrl = apiUrl("auth/logout");
    fetch(logoutUrl, {
      method: "POST",
      credentials: "include",
      keepalive: true,
      headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" }
    }).catch(() => { });
  } catch { }

  // Explicitamente remover cookie de auth_token en cliente
  document.cookie = "auth_token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;";

  // Redirigir al login (SPA, sin recarga)
  window.location.hash = "#/login";
  window.dispatchEvent(new HashChangeEvent("hashchange"));
}

export function resetAuthSystem() {
  logoutInProgress = false;
  alertShown = false;
  countdownShown = false;
  lastServerCheckTime = 0;
  consecutiveErrors = 0;

  stopCheckSessionMonitor();
  stopExpiryCheckInterval();

  if (expirationTimer !== null) {
    clearTimeout(expirationTimer);
    expirationTimer = null;
  }
  if (warningTimer !== null) {
    clearTimeout(warningTimer);
    warningTimer = null;
  }

  sessionStorage.removeItem(SS_AUTH);
}

// ===================== Monitor de Storage (detecta cuando se borra el token) =====================
function checkTokenExists() {
  if (logoutInProgress) return;

  const isAuth = sessionStorage.getItem(SS_AUTH) === "true";
  const hasToken = !!getToken();
  const hasUser = !!get(K_USER);

  // Si estábamos autenticados pero ya no hay token ni usuario, la sesión se perdió
  if (isAuth && !hasToken && !hasUser) {
    if (!logoutInProgress) {
      logout(true, "Su sesión ha expirado").catch(console.error);
    }
  }
}

function monitorTokenStorage() {
  // Verificar cada 50ms si el token sigue existiendo (detección prácticamente instantánea)
  setInterval(checkTokenExists, 50);

  // ✅ CRÍTICO: Verificar INMEDIATAMENTE en cada navegación
  window.addEventListener('hashchange', checkTokenExists, { capture: true });

  // ✅ Verificar también cuando la ventana recupera el foco
  window.addEventListener('focus', checkTokenExists);

  // ✅ Verificar en popstate (navegación con botones atrás/adelante)
  window.addEventListener('popstate', checkTokenExists, { capture: true });

  // ✅ Verificar en click de enlaces internos
  document.addEventListener('click', (e) => {
    const target = e.target as HTMLElement;
    if (target.closest('a[href^="#"]')) {
      checkTokenExists();
    }
  }, { capture: true });
}

// ===================== Boot de la app =====================
export async function setupAuthOnAppStart() {

  const expiryTs = getExpiryTimestamp();
  if (expiryTs > 0) {
    const remaining = getTokenTimeRemaining();


    if (remaining > 0) {
      sessionStorage.setItem(SS_AUTH, "true");
      scheduleTokenExpiration();
    } else {
      console.warn("[auth] ⏰ Sesión previa ya expirada");
      resetAuthSystem();
    }
  }

  startCheckSessionMonitor();

  // ✅ Iniciar monitor de storage para detectar cuando se borra el token
  monitorTokenStorage();
}

// ===================== Helpers de UI =====================
export const isSessionActive = () => isAuthenticated();

export function onAuthExpiryTick(cb: (ms: number) => void): () => void {
  const h = (e: Event) => cb((e as CustomEvent).detail?.remainingMs ?? 0);
  window.addEventListener("auth:expiry-tick", h as any);
  return () => window.removeEventListener("auth:expiry-tick", h as any);
}

export function getMonitorStatus() {
  const remaining = getTokenTimeRemaining();
  return {
    checkSessionMonitorActivo: checkSessionInterval !== null && USE_CHECK_MONITOR,
    expirationTimerActivo: expirationTimer !== null,
    warningTimerActivo: warningTimer !== null,
    expiryCheckIntervalActivo: expiryCheckInterval !== null,
    logoutEnProgreso: logoutInProgress,
    ultimaVerificacionServidor: lastServerCheckTime,
    erroresConsecutivos: consecutiveErrors,
    tiempoRestante: remaining,
    tiempoRestanteFormateado: `${Math.floor(remaining / 60000)}m ${Math.floor((remaining % 60000) / 1000)}s`,
    expiryTimestamp: getExpiryTimestamp(),
    expiryDate: getExpiryTimestamp() > 0 ? new Date(getExpiryTimestamp() * 1000).toLocaleString() : "N/A",
    currentTime: new Date().toLocaleString(),
    sesionActiva: isAuthenticated()
  };
}